import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/Layout';
import defaultApi, { ordersApi } from '@/services/api';

// ── Fabric Color Mapping ──
interface FabricColor {
  name: string;
  rgba: string;
}

const FABRIC_COLORS: Record<string, FabricColor[]> = {
  omega_pro_3: [
    { name: 'Brown/Taupe', rgba: 'rgba(120,90,65,0.55)' },
    { name: 'White', rgba: 'rgba(240,240,240,0.45)' },
    { name: 'White/Linen', rgba: 'rgba(235,225,210,0.45)' },
    { name: 'White Gray', rgba: 'rgba(210,210,215,0.45)' },
    { name: 'Light Gray', rgba: 'rgba(175,175,175,0.50)' },
    { name: 'Black/Bronze', rgba: 'rgba(40,30,25,0.60)' },
    { name: 'Gray/Black', rgba: 'rgba(60,60,60,0.60)' },
    { name: 'Black', rgba: 'rgba(20,20,20,0.65)' },
    { name: 'Charcoal/Taupe', rgba: 'rgba(85,75,70,0.55)' },
  ],
  omega_pro_5: [], // filled below
  omega_pro_10: [], // filled below
  twitchell_80: [{ name: 'Black', rgba: 'rgba(15,15,15,0.55)' }],
  twitchell_90: [{ name: 'Black', rgba: 'rgba(15,15,15,0.65)' }],
  twitchell_95: [{ name: 'Black', rgba: 'rgba(15,15,15,0.72)' }],
  twitchell_nano_95: [{ name: 'Black', rgba: 'rgba(10,10,10,0.72)' }],
  twitchell_nano_97: [{ name: 'Black', rgba: 'rgba(10,10,10,0.78)' }],
  twitchell_nano_99: [{ name: 'Black', rgba: 'rgba(10,10,10,0.85)' }],
  twitchell_dimout: [{ name: 'Black', rgba: 'rgba(5,5,5,0.92)' }],
  '502_vinyl': [
    { name: 'Black', rgba: 'rgba(15,15,15,0.55)' },
    { name: 'Red (Poppy)', rgba: 'rgba(180,40,40,0.50)' },
    { name: 'White', rgba: 'rgba(240,240,240,0.45)' },
    { name: 'Blue (Midnight)', rgba: 'rgba(25,35,80,0.55)' },
    { name: 'Anthracite', rgba: 'rgba(50,50,55,0.55)' },
    { name: 'Grey (Concrete)', rgba: 'rgba(140,140,140,0.50)' },
    { name: 'Boulder', rgba: 'rgba(110,105,100,0.50)' },
    { name: 'Antelope', rgba: 'rgba(155,135,110,0.48)' },
    { name: 'Tan (Pepper)', rgba: 'rgba(170,145,110,0.48)' },
    { name: 'Hemp', rgba: 'rgba(145,130,100,0.48)' },
  ],
  nano_55_insect: [{ name: 'Standard', rgba: 'rgba(30,30,30,0.35)' }],
};
// Omega Pro 5/10 share same colors as 3
FABRIC_COLORS.omega_pro_5 = FABRIC_COLORS.omega_pro_3;
FABRIC_COLORS.omega_pro_10 = FABRIC_COLORS.omega_pro_3;

const FABRIC_TYPE_LABELS: Record<string, string> = {
  omega_pro_3: 'Omega Pro 3%',
  omega_pro_5: 'Omega Pro 5%',
  omega_pro_10: 'Omega Pro 10%',
  twitchell_80: 'Twitchell 80',
  twitchell_90: 'Twitchell 90',
  twitchell_95: 'Twitchell 95',
  twitchell_nano_95: 'Twitchell Nano 95',
  twitchell_nano_97: 'Twitchell Nano 97',
  twitchell_nano_99: 'Twitchell Nano 99',
  twitchell_dimout: 'Twitchell Dimout',
  '502_vinyl': '502 Vinyl',
  nano_55_insect: 'Twitchell Nano 55 Insect',
};

const FRAME_COLORS = [
  { name: 'Black', value: '#1a1a1a' },
  { name: 'Bronze', value: '#5c3a1e' },
  { name: 'White', value: '#f5f5f5' },
  { name: 'Tan', value: '#c2a87d' },
  { name: 'Mill Finish', value: '#c0c0c0' },
];

interface Point {
  x: number;
  y: number;
}

interface FinalizedScreen {
  points: Point[];
  fabricColor: string;
  frameColor: string;
  number: number;
  label: string;
  fabricType: string;
  fabricColorName: string;
  frameColorName: string;
  productType: 'vtrack' | 'door' | 'fixed';
}

const MAX_SCREENS = 999; // No practical limit

interface CompletedOpening {
  imageDataUrl: string;
  imageSrc: string;
  screens: FinalizedScreen[];
  label: string;
}

// ── Geometry helpers ──
function doLinesIntersect(p1: Point, p2: Point, p3: Point, p4: Point): boolean {
  const d = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
  if (d === 0) return false;
  const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / d;
  const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / d;
  return ua > 0 && ua < 1 && ub > 0 && ub < 1;
}

function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    if (((yi > point.y) !== (yj > point.y)) && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

function isPolygonSelfIntersecting(points: Point[]): boolean {
  if (points.length < 4) return false;
  const edges: [Point, Point][] = [];
  for (let i = 0; i < points.length; i++) {
    edges.push([points[i], points[(i + 1) % points.length]]);
  }
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 2; j < edges.length; j++) {
      if (i === 0 && j === edges.length - 1) continue;
      if (doLinesIntersect(edges[i][0], edges[i][1], edges[j][0], edges[j][1])) return true;
    }
  }
  return false;
}

// ── Canvas drawing helpers ──
function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawScreen(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  fabricColor: string,
  frameColor: string,
  number: number,
  label: string,
  screenProductType: 'vtrack' | 'door' | 'fixed' = 'vtrack',
  hideLabels = false,
) {
  if (screenProductType === 'door') {
    // ── Door: fabric fill + mesh texture + thick frame + handle ──
    ctx.fillStyle = fabricColor || 'rgba(60,60,60,0.3)';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.fill();
    // Mesh grid lines
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.clip();
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    ctx.strokeStyle = 'rgba(150,150,150,0.2)';
    ctx.lineWidth = 0.5;
    for (let y = minY; y <= maxY; y += 6) {
      ctx.beginPath(); ctx.moveTo(minX, y); ctx.lineTo(maxX, y); ctx.stroke();
    }
    for (let x = minX; x <= maxX; x += 6) {
      ctx.beginPath(); ctx.moveTo(x, minY); ctx.lineTo(x, maxY); ctx.stroke();
    }
    ctx.restore();
    // Thick frame outline
    ctx.strokeStyle = frameColor;
    ctx.lineWidth = 8;
    ctx.lineJoin = 'miter';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.stroke();
    // Door handle
    try {
      const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
      const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
      let rightX = -Infinity, handlePt = { x: cx, y: cy };
      for (let i = 0; i < points.length; i++) {
        const mid = { x: (points[i].x + points[(i + 1) % points.length].x) / 2, y: (points[i].y + points[(i + 1) % points.length].y) / 2 };
        if (mid.x > rightX) { rightX = mid.x; handlePt = mid; }
      }
      ctx.fillStyle = frameColor;
      ctx.beginPath();
      ctx.arc(handlePt.x - 14, handlePt.y, 4, 0, Math.PI * 2);
      ctx.fill();
    } catch { /* skip handle on error */ }
  } else {
    // Fabric fill (V-Track and Fixed)
    ctx.fillStyle = fabricColor;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.fill();
  }

  // Frame outline — V-Track only (fixed screens have no frame, doors handled above)
  if (screenProductType === 'vtrack' && points.length === 4) {
    // Identify top edge (housing) by smallest average Y
    const edges = points.map((p, i) => {
      const next = points[(i + 1) % 4];
      return { midY: (p.y + next.y) / 2, index: i };
    });
    const topEdge = edges.reduce((min, e) => e.midY < min.midY ? e : min).index;

    // Draw frame outline on all 4 edges — closed path so corners meet exactly at points
    ctx.strokeStyle = frameColor;
    ctx.lineWidth = 6;
    ctx.lineJoin = 'miter';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < 4; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.stroke();

    // Draw housing box as filled band extending inward from top edge
    // Inner corners follow adjacent side edges so housing stays true even on angled openings
    const housingThickness = 12;
    const A = points[topEdge];
    const B = points[(topEdge + 1) % 4];
    // D is the point connected to A on the other side (previous point)
    const D = points[(topEdge + 3) % 4];
    // C is the point connected to B on the other side (next point)
    const C = points[(topEdge + 2) % 4];

    // Direction from A toward D (left side edge, inward)
    const adx = D.x - A.x, ady = D.y - A.y;
    const adLen = Math.sqrt(adx * adx + ady * ady) || 1;
    // Direction from B toward C (right side edge, inward)
    const bcx = C.x - B.x, bcy = C.y - B.y;
    const bcLen = Math.sqrt(bcx * bcx + bcy * bcy) || 1;

    // Inner corners: offset A along A→D direction, offset B along B→C direction
    const Ai = { x: A.x + (adx / adLen) * housingThickness, y: A.y + (ady / adLen) * housingThickness };
    const Bi = { x: B.x + (bcx / bcLen) * housingThickness, y: B.y + (bcy / bcLen) * housingThickness };

    ctx.fillStyle = frameColor;
    ctx.beginPath();
    ctx.moveTo(A.x, A.y);
    ctx.lineTo(B.x, B.y);
    ctx.lineTo(Bi.x, Bi.y);
    ctx.lineTo(Ai.x, Ai.y);
    ctx.closePath();
    ctx.fill();
  } else if (screenProductType === 'vtrack') {
    ctx.strokeStyle = frameColor;
    ctx.lineWidth = 6;
    ctx.lineJoin = 'miter';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.stroke();
  } else {
    // Fixed screens: thin dashed border for visibility
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (hideLabels) {
    ctx.textAlign = 'left';
    return;
  }

  // Badge position
  const topLeft = points.reduce(
    (min, p) => ({ x: Math.min(min.x, p.x), y: Math.min(min.y, p.y) }),
    { x: Infinity, y: Infinity },
  );
  const badgeX = topLeft.x + 8;
  const badgeY = topLeft.y + 8;

  // Badge background
  const badgeColor = screenProductType === 'door' ? '#60a5fa' : screenProductType === 'fixed' ? '#14b8a6' : '#ffcd24';
  const badgeWidth = (screenProductType === 'door' || screenProductType === 'fixed') ? 28 : 24;
  ctx.fillStyle = badgeColor;
  drawRoundedRect(ctx, badgeX, badgeY, badgeWidth, 22, 4);
  ctx.fill();

  // Badge text
  const badgePrefix = screenProductType === 'door' ? 'D' : screenProductType === 'fixed' ? 'F' : '';
  ctx.fillStyle = (screenProductType === 'door' || screenProductType === 'fixed') ? '#fff' : '#1a1a1a';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(badgePrefix + number.toString(), badgeX + badgeWidth / 2, badgeY + 16);

  // Label below badge
  if (label) {
    ctx.font = '11px sans-serif';
    const displayLabel = screenProductType === 'door' ? `Door - ${label}` : screenProductType === 'fixed' ? `Fixed - ${label}` : label;
    const labelWidth = ctx.measureText(displayLabel).width + 8;
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(badgeX, badgeY + 26, labelWidth, 16);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'left';
    ctx.fillText(displayLabel, badgeX + 4, badgeY + 38);
  }

  ctx.textAlign = 'left';
}

export function ScreenDesignerPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [finalizedScreens, setFinalizedScreens] = useState<FinalizedScreen[]>([]);
  const [currentScreenNumber, setCurrentScreenNumber] = useState(1);

  const [productType, setProductType] = useState<'vtrack' | 'door' | 'fixed'>('vtrack');
  const [fabricType, setFabricType] = useState('');
  const [fabricColor, setFabricColor] = useState('');
  const [frameColor, setFrameColor] = useState('');

  const [completedOpenings, setCompletedOpenings] = useState<CompletedOpening[]>([]);
  const [globalScreenNumber, setGlobalScreenNumber] = useState(1);

  const [hideOverlays, setHideOverlays] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragIndex, setDragIndex] = useState(-1);
  const touchStartPos = useRef<Point | null>(null);

  // Save section state
  const [loadedDesignId, setLoadedDesignId] = useState<string | null>(null);
  const [designName, setDesignName] = useState('');
  const [linkType, setLinkType] = useState('');
  const [linkId, setLinkId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dealerOrders, setDealerOrders] = useState<{ id: string; orderNumber: string; sidemark: string; status: string }[]>([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');

  // Auto-link from order detail page + pre-load orders for link dropdowns
  useEffect(() => {
    const orderData = sessionStorage.getItem('orderToDesigner');
    if (orderData) {
      try {
        const data = JSON.parse(orderData);
        sessionStorage.removeItem('orderToDesigner');
        setLinkType('order');
        setLinkId(data.orderNumber || data.orderId || '');
      } catch (e) {
        console.error('Failed to load order context for designer:', e);
      }
    }
    // Pre-load orders so they're ready for link-to dropdowns
    (defaultApi as any).get('/orders', { params: { pageSize: 100 } }).then(({ data }: any) => {
      const raw = Array.isArray(data) ? data : (data?.data || data?.orders || []);
      setDealerOrders(raw.map((o: any) => ({
        id: o.id,
        orderNumber: o.orderNumber || o.order_number || '',
        sidemark: o.sidemark || '',
        status: o.status || '',
      })));
      setOrdersLoaded(true);
    }).catch((err: any) => { console.error('[ScreenDesigner] Orders load failed:', err?.message || err); setOrdersLoaded(true); });
  }, []);

  // Saved designs list
  const [savedDesigns, setSavedDesigns] = useState<any[]>([]);
  const [showSavedDesigns, setShowSavedDesigns] = useState(false);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [expandedDesignId, setExpandedDesignId] = useState<string | null>(null);
  const [editingDesignId, setEditingDesignId] = useState<string | null>(null);
  const [editDesignName, setEditDesignName] = useState('');
  const [editDesignNotes, setEditDesignNotes] = useState('');
  const [editDesignLinkType, setEditDesignLinkType] = useState('');
  const [editDesignLinkId, setEditDesignLinkId] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fabricColors = fabricType && FABRIC_COLORS[fabricType] ? FABRIC_COLORS[fabricType] : [];

  // ── Canvas setup & redraw ──
  const setupCanvas = useCallback(
    (img: HTMLImageElement) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const maxWidth = 900;
      const maxHeight = 650;
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
    },
    [],
  );

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImage) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);

    // Draw finalized screens
    finalizedScreens.forEach((s) => {
      drawScreen(ctx, s.points, s.fabricColor, s.frameColor, s.number, s.label, s.productType, hideOverlays);
    });

    // Draw current preview
    if (currentPoints.length > 0) {
      const canPreviewVtrack = productType === 'vtrack' && currentPoints.length === 4 && fabricColor && frameColor;
      const canPreviewDoor = productType === 'door' && currentPoints.length === 4 && fabricColor && frameColor;
      const canPreviewFixed = productType === 'fixed' && currentPoints.length >= 3 && fabricColor;
      if (canPreviewVtrack || canPreviewDoor || canPreviewFixed) {
        drawScreen(ctx, currentPoints, fabricColor || 'transparent', frameColor || 'transparent', currentScreenNumber, '', productType);
      }

      // Connection lines
      if (currentPoints.length > 1) {
        ctx.strokeStyle = productType === 'door' ? '#60a5fa' : productType === 'fixed' ? '#14b8a6' : '#ffcd24';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(currentPoints[0].x, currentPoints[0].y);
        for (let i = 1; i < currentPoints.length; i++) {
          ctx.lineTo(currentPoints[i].x, currentPoints[i].y);
        }
        const shouldClose = ((productType === 'vtrack' || productType === 'door') && currentPoints.length === 4) || (productType === 'fixed' && currentPoints.length >= 3);
        if (shouldClose) ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draggable corner points
      const pointColor = productType === 'door' ? '#60a5fa' : productType === 'fixed' ? '#14b8a6' : '#ffcd24';
      currentPoints.forEach((point, index) => {
        ctx.fillStyle = pointColor;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 10, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#1a1a1a';
        ctx.beginPath();
        ctx.arc(point.x, point.y, 6, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = pointColor;
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText((index + 1).toString(), point.x, point.y + 4);
      });
    }

    ctx.textAlign = 'left';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalImage, finalizedScreens, currentPoints, fabricColor, frameColor, currentScreenNumber, productType, hideOverlays]);

  // Redraw canvas whenever any drawing-related state changes
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Force canvas redraw on finalized screens change (belt-and-suspenders)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImage) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);
    finalizedScreens.forEach((s) => {
      drawScreen(ctx, s.points, s.fabricColor, s.frameColor, s.number, s.label, s.productType, hideOverlays);
    });
    if (currentPoints.length > 0) {
      const cp = currentPoints;
      if ((productType === 'vtrack' || productType === 'door') && cp.length === 4 && fabricColor && frameColor) {
        drawScreen(ctx, cp, fabricColor, frameColor, currentScreenNumber, '', productType);
      } else if (productType === 'fixed' && cp.length >= 3 && fabricColor) {
        drawScreen(ctx, cp, fabricColor, frameColor || 'transparent', currentScreenNumber, '', productType);
      }
      if (cp.length > 1) {
        ctx.strokeStyle = productType === 'door' ? '#60a5fa' : productType === 'fixed' ? '#14b8a6' : '#ffcd24';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(cp[0].x, cp[0].y);
        for (let i = 1; i < cp.length; i++) ctx.lineTo(cp[i].x, cp[i].y);
        if (((productType === 'vtrack' || productType === 'door') && cp.length === 4) || (productType === 'fixed' && cp.length >= 3)) ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
      }
      const pc = productType === 'door' ? '#60a5fa' : productType === 'fixed' ? '#14b8a6' : '#ffcd24';
      cp.forEach((point, index) => {
        ctx.fillStyle = pc;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 10, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = '#1a1a1a';
        ctx.beginPath();
        ctx.arc(point.x, point.y, 6, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = pc;
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText((index + 1).toString(), point.x, point.y + 4);
      });
      ctx.textAlign = 'left';
    }
  }, [finalizedScreens, currentPoints, originalImage, fabricColor, frameColor, currentScreenNumber, productType, hideOverlays]);

  // ── Event handlers ──
  const getEventPos = (e: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    let clientX: number, clientY: number;
    if ('touches' in e && e.touches[0]) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if ('clientX' in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return { x: 0, y: 0 };
    }
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (!originalImage) return;
    e.preventDefault();
    const pos = getEventPos(e);
    // Track touch start position for tap detection
    if ('touches' in e) {
      touchStartPos.current = pos;
    }
    for (let i = 0; i < currentPoints.length; i++) {
      const dist = Math.hypot(pos.x - currentPoints[i].x, pos.y - currentPoints[i].y);
      if (dist <= 14) {
        setIsDragging(true);
        setDragIndex(i);
        return;
      }
    }
    setDragIndex(-1);
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!originalImage) return;
    e.preventDefault();
    const pos = getEventPos(e);

    if (isDragging && dragIndex >= 0) {
      const newPoints = [...currentPoints];
      newPoints[dragIndex] = pos;
      if (newPoints.length < 4 || !isPolygonSelfIntersecting(newPoints)) {
        setCurrentPoints(newPoints);
      }
    }
  };

  const handlePointerUp = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const wasDragging = isDragging;
    setIsDragging(false);
    setDragIndex(-1);

    // On touch devices, e.preventDefault() in touchstart kills the synthetic click,
    // so we handle tap-to-place-point here instead of in handleCanvasClick.
    if ('changedTouches' in e && touchStartPos.current && !wasDragging) {
      const endPos = (() => {
        const canvas = canvasRef.current;
        if (!canvas || !e.changedTouches[0]) return null;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
          x: (e.changedTouches[0].clientX - rect.left) * scaleX,
          y: (e.changedTouches[0].clientY - rect.top) * scaleY,
        };
      })();
      if (endPos) {
        const dist = Math.hypot(endPos.x - touchStartPos.current.x, endPos.y - touchStartPos.current.y);
        // Tap = finger moved less than 10px
        if (dist < 10) {
          const maxPoints = productType === 'fixed' ? 20 : 4;
          if (originalImage && currentPoints.length < maxPoints && finalizedScreens.length < MAX_SCREENS) {
            // Don't add if tapping near existing point
            let nearExisting = false;
            for (let i = 0; i < currentPoints.length; i++) {
              if (Math.hypot(endPos.x - currentPoints[i].x, endPos.y - currentPoints[i].y) <= 14) {
                nearExisting = true;
                break;
              }
            }
            if (!nearExisting) {
              if (currentPoints.length >= 3) {
                const testPoints = [...currentPoints, endPos];
                if (isPolygonSelfIntersecting(testPoints)) {
                  alert("Select corners in order around the opening — don't cross lines.");
                  touchStartPos.current = null;
                  return;
                }
              }
              setCurrentPoints((prev) => [...prev, endPos]);
            }
          }
        }
      }
      touchStartPos.current = null;
    }
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (!originalImage || isDragging) return;

    const pos = getEventPos(e);

    // Check if clicking on a finalized screen — select it for editing
    if (currentPoints.length === 0) {
      for (let i = finalizedScreens.length - 1; i >= 0; i--) {
        const s = finalizedScreens[i];
        if (pointInPolygon(pos, s.points)) {
          // Remove from finalized and load into current editing
          setCurrentPoints(s.points);
          setProductType(s.productType);
          setFabricColor(s.fabricColor);
          setFrameColor(s.frameColor);
          setFabricType(s.fabricType);
          setFinalizedScreens(prev => {
            const updated = prev.filter((_, idx) => idx !== i);
            return updated.map((fs, idx) => ({ ...fs, number: idx + 1, label: `Screen ${idx + 1}` }));
          });
          setCurrentScreenNumber(finalizedScreens.length); // will be re-numbered
          return;
        }
      }
    }

    const maxPoints = productType === 'fixed' ? 20 : 4;
    if (currentPoints.length >= maxPoints) return;
    if (finalizedScreens.length >= MAX_SCREENS) return;

    // Don't add if clicking near existing point
    for (let i = 0; i < currentPoints.length; i++) {
      if (Math.hypot(pos.x - currentPoints[i].x, pos.y - currentPoints[i].y) <= 14) return;
    }

    if (currentPoints.length >= 3) {
      const testPoints = [...currentPoints, pos];
      if (isPolygonSelfIntersecting(testPoints)) {
        alert("Select corners in order around the opening — don't cross lines.");
        return;
      }
    }

    setCurrentPoints((prev) => [...prev, pos]);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setupCanvas(img);
        // Reset state
        setCurrentPoints([]);
        setFinalizedScreens([]);
        setCurrentScreenNumber(1);
        setProductType('vtrack');
        setFabricType('');
        setFabricColor('');
        setFrameColor('');
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const undoPoint = () => {
    setCurrentPoints((prev) => prev.slice(0, -1));
  };

  const finalizeScreen = () => {
    if (productType === 'vtrack' || productType === 'door') {
      if (currentPoints.length !== 4 || !fabricColor || !frameColor) return;
    } else {
      if (currentPoints.length < 3 || !fabricColor) return;
    }
    if (finalizedScreens.length >= MAX_SCREENS) return;

    const fabricTypeName = FABRIC_TYPE_LABELS[fabricType] || fabricType;
    const fabricColorObj = fabricColors.find((c) => c.rgba === fabricColor);
    const fabricColorName = fabricColorObj?.name || '';
    const frameObj = FRAME_COLORS.find((c) => c.value === frameColor);
    const frameColorName = productType === 'fixed' ? 'None' : (frameObj?.name || '');

    const fabricLabel = `${fabricTypeName} - ${fabricColorName}`;
    const label = productType === 'door'
      ? `Door - ${fabricLabel}`
      : productType === 'fixed'
      ? `Fixed - ${fabricLabel}`
      : fabricLabel;

    setFinalizedScreens((prev) => [
      ...prev,
      {
        points: [...currentPoints],
        fabricColor,
        frameColor: productType === 'fixed' ? 'transparent' : frameColor,
        number: currentScreenNumber,
        label,
        fabricType,
        fabricColorName,
        frameColorName,
        productType,
      },
    ]);

    setCurrentPoints([]);
    setCurrentScreenNumber((n) => n + 1);
    setFabricType('');
    setFabricColor('');
    setFrameColor('');
  };

  const resetAll = () => {
    setCurrentPoints([]);
    setFinalizedScreens([]);
    setCurrentScreenNumber(1);
    setProductType('vtrack');
    setFabricType('');
    setFabricColor('');
    setFrameColor('');
  };

  // ── Save current opening & prepare for next ──
  const saveCurrentOpening = () => {
    if (finalizedScreens.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const imageDataUrl = canvas.toDataURL('image/png');
    const imageSrc = originalImage?.src || '';
    const openingNum = completedOpenings.length + 1;

    setCompletedOpenings((prev) => [
      ...prev,
      {
        imageDataUrl,
        imageSrc,
        screens: [...finalizedScreens],
        label: `Opening ${openingNum} — ${finalizedScreens.length} screen${finalizedScreens.length !== 1 ? 's' : ''}`,
      },
    ]);

    // Track global screen numbering across openings
    const nextScreenNum = globalScreenNumber + finalizedScreens.length;
    setGlobalScreenNumber(nextScreenNum);

    // Reset canvas for next opening
    setOriginalImage(null);
    setCurrentPoints([]);
    setFinalizedScreens([]);
    setCurrentScreenNumber(nextScreenNum);
    setProductType('vtrack');
    setFabricType('');
    setFabricColor('');
    setFrameColor('');
  };

  const removeOpening = (index: number) => {
    setCompletedOpenings((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Save Design (all openings) ──
  const handleSaveDesign = async () => {
    // Collect all openings to save
    const openingsToSave: { imageData: string; screens: FinalizedScreen[] }[] = [];

    // Add completed openings
    completedOpenings.forEach((o) => {
      openingsToSave.push({ imageData: o.imageDataUrl, screens: o.screens });
    });

    // Add current canvas if it has finalized screens
    if (finalizedScreens.length > 0 && canvasRef.current) {
      openingsToSave.push({
        imageData: canvasRef.current.toDataURL('image/png'),
        screens: [...finalizedScreens],
      });
    }

    if (!designName.trim() || openingsToSave.length === 0) return;
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const savedIds: string[] = [];

      for (let i = 0; i < openingsToSave.length; i++) {
        const opening = openingsToSave[i];
        const screensPayload = opening.screens.map((s) => ({
          number: s.number,
          fabric_type: s.fabricType,
          fabric_color: s.fabricColorName,
          frame_color: s.frameColorName,
          points: s.points,
          product_type: s.productType,
        }));

        const suffix = openingsToSave.length > 1 ? ` — Opening ${i + 1}` : '';
        const res = await fetch(`${apiUrl}/screen-designs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: `${designName.trim()}${suffix}`,
            image: opening.imageData,
            screens: screensPayload,
            link_type: linkType || undefined,
            link_id: linkId || undefined,
            notes: notes || undefined,
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ message: `Server error (${res.status}). The image may be too large — try a smaller photo.` }));
          throw new Error(err.message || `Failed to save opening ${i + 1}`);
        }

        const result = await res.json();
        savedIds.push(result.id);
      }

      setSaveSuccess(`${savedIds.length} opening${savedIds.length !== 1 ? 's' : ''} saved!`);
      setDesignName('');
      setLinkType('');
      setLinkId('');
      setNotes('');
      setLoadedDesignId(null);
      setCompletedOpenings([]);
      setFinalizedScreens([]);
      setOriginalImage(null);
      setCurrentPoints([]);
      setCurrentScreenNumber(1);
      setGlobalScreenNumber(1);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save design');
    } finally {
      setSaving(false);
    }
  };

  // ── Load saved designs ──
  const loadSavedDesigns = async () => {
    setLoadingDesigns(true);
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/screen-designs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSavedDesigns(await res.json());
      }
    } catch {
      // silent
    } finally {
      setLoadingDesigns(false);
    }
  };

  const loadDealerOrders = async () => {
    if (ordersLoaded) return;
    try {
      const { data }: any = await (defaultApi as any).get('/orders', { params: { pageSize: 100 } });
      const raw = Array.isArray(data) ? data : (data?.data || data?.orders || []);
      setDealerOrders(raw.map((o: any) => ({
        id: o.id,
        orderNumber: o.orderNumber || o.order_number || '',
        sidemark: o.sidemark || '',
        status: o.status || '',
      })));
      setOrdersLoaded(true);
    } catch (err: any) {
      console.error('[ScreenDesigner] Orders load failed:', err?.message || err);
      setOrdersLoaded(true);
    }
  };

  const loadDesignDetail = async (designId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/screen-designs/${designId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const full = await res.json();
        setSavedDesigns(prev => prev.map(d => d.id === designId ? { ...d, ...full, _loaded: true } : d));
      }
    } catch {
      // silent
    }
  };

  const handleExpandDesign = (designId: string) => {
    if (expandedDesignId === designId) {
      setExpandedDesignId(null);
      setEditingDesignId(null);
      setDeleteConfirmId(null);
    } else {
      setExpandedDesignId(designId);
      setEditingDesignId(null);
      setDeleteConfirmId(null);
      const d = savedDesigns.find(x => x.id === designId);
      if (!d?._loaded) loadDesignDetail(designId);
    }
  };

  const handleStartEditDesign = (d: any) => {
    setEditingDesignId(d.id);
    setEditDesignName(d.name || '');
    setEditDesignNotes(d.notes || '');
    setEditDesignLinkType(d.link_type || '');
    setEditDesignLinkId(d.link_id || '');
    setDeleteConfirmId(null);
    if (d.link_type === 'order') loadDealerOrders();
  };

  const handleSaveEditDesign = async (designId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/screen-designs/${designId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editDesignName.trim(),
          notes: editDesignNotes.trim() || null,
          link_type: editDesignLinkType || null,
          link_id: editDesignLinkId || null,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setSavedDesigns(prev => prev.map(d => d.id === designId ? { ...d, ...updated, _loaded: true } : d));
        setEditingDesignId(null);
      }
    } catch {
      // silent
    }
  };

  const handleDeleteDesign = async (designId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/screen-designs/${designId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSavedDesigns(prev => prev.filter(d => d.id !== designId));
        setExpandedDesignId(null);
        setDeleteConfirmId(null);
      }
    } catch {
      // silent
    }
  };

  const handleOpenInEditor = (d: any) => {
    if (!d.image_url || !d.screens) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setOriginalImage(img);
      setupCanvas(img);
      const screens: FinalizedScreen[] = (d.screens || []).map((s: any, i: number) => {
        // Resolve color names back to CSS values
        const frameMatch = FRAME_COLORS.find(f => f.name === s.frame_color);
        const fabricColorValue = (() => {
          // Search all fabric type color lists for a matching name
          for (const colors of Object.values(FABRIC_COLORS)) {
            const match = colors.find(c => c.name === s.fabric_color);
            if (match) return match.rgba;
          }
          return s.fabric_color || '';
        })();
        return {
          points: s.points || [],
          fabricType: s.fabric_type || 'omega-3',
          fabricColor: fabricColorValue,
          frameColor: frameMatch?.value || s.frame_color || '',
          fabricColorName: s.fabric_color || '',
          frameColorName: s.frame_color || '',
          number: s.number || i + 1,
          label: `Screen ${s.number || i + 1}`,
          productType: s.product_type || 'vtrack',
        };
      });
      setFinalizedScreens(screens);
      setCurrentScreenNumber(screens.length + 1);
      setCurrentPoints([]);
      // Track which design is loaded so save can update it
      setLoadedDesignId(d.id);
      setDesignName(d.name || '');
      setNotes(d.notes || '');
      setLinkType(d.link_type || '');
      setLinkId(d.link_id || '');
      setShowSavedDesigns(false);
      setExpandedDesignId(null);
    };
    img.src = d.image_url;
  };

  const canFinalize = (productType === 'vtrack' || productType === 'door')
    ? currentPoints.length === 4 && fabricColor !== '' && frameColor !== '' && finalizedScreens.length < MAX_SCREENS
    : currentPoints.length >= 3 && fabricColor !== '' && finalizedScreens.length < MAX_SCREENS;

  return (
    <div>
      <PageHeader
        title="Screen Designer"
        subtitle="Upload a photo, define screen areas, choose fabrics, and save designs"
        actions={
          <button
            onClick={() => {
              setShowSavedDesigns(!showSavedDesigns);
              if (!showSavedDesigns) loadSavedDesigns();
            }}
            className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 transition-colors text-sm font-medium"
          >
            {showSavedDesigns ? 'Hide' : 'My Designs'}
          </button>
        }
      />

      {/* Saved Designs Panel */}
      {showSavedDesigns && (
        <div className="card p-4 mb-6 border-l-4 border-l-blue-500">
          <h3 className="text-white font-semibold mb-3">Saved Designs</h3>
          {loadingDesigns ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : savedDesigns.length === 0 ? (
            <p className="text-gray-400 text-sm">No saved designs yet.</p>
          ) : (
            <div className="space-y-3">
              {savedDesigns.map((d: any) => {
                const isExpanded = expandedDesignId === d.id;
                const isEditing = editingDesignId === d.id;
                const isDeleting = deleteConfirmId === d.id;
                const screens = d.screens || [];
                return (
                  <div key={d.id} className={`bg-keder-dark border rounded-lg overflow-hidden transition-colors ${isExpanded ? 'border-keder-yellow' : 'border-keder-gray hover:border-keder-yellow/50'}`}>
                    {/* Collapsed row — always visible */}
                    <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => handleExpandDesign(d.id)}>
                      {d.image_url && (
                        <img src={d.image_url} alt={d.name} className="w-16 h-12 object-cover rounded flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{d.name}</p>
                        <p className="text-gray-400 text-xs">
                          {d.screen_count} screen{d.screen_count !== 1 ? 's' : ''} &bull; {new Date(d.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <svg className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t border-keder-gray p-4">
                        {/* Full image */}
                        {d.image_url && (
                          <img src={d.image_url} alt={d.name} className="w-full max-h-64 object-contain rounded-lg mb-4 bg-black/30" />
                        )}

                        {/* Screen specs */}
                        {screens.length > 0 && (
                          <div className="mb-4">
                            <p className="text-gray-400 text-xs font-medium mb-2">SCREENS</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {screens.map((s: any, i: number) => (
                                <div key={i} className="bg-keder-gray/30 rounded p-2 text-xs">
                                  <span className="text-keder-yellow font-medium">Screen {s.number || i + 1}</span>
                                  <span className="text-gray-400 ml-2">
                                    {s.fabric_type || s.fabricType || '—'} / {s.fabric_color || s.fabricColor || '—'}
                                  </span>
                                  {(s.frame_color || s.frameColor) && (
                                    <span className="text-gray-500 ml-1">({s.frame_color || s.frameColor})</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {d.notes && !isEditing && (
                          <p className="text-gray-400 text-xs mb-4"><span className="text-gray-500">Notes:</span> {d.notes}</p>
                        )}

                        {/* Edit form */}
                        {isEditing ? (
                          <div className="space-y-3 mb-4">
                            <div>
                              <label className="text-gray-400 text-xs block mb-1">Name</label>
                              <input
                                type="text"
                                value={editDesignName}
                                onChange={e => setEditDesignName(e.target.value)}
                                className="w-full bg-keder-gray/50 text-white text-sm rounded px-3 py-2 border border-keder-gray focus:border-keder-yellow outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-gray-400 text-xs block mb-1">Notes</label>
                              <textarea
                                value={editDesignNotes}
                                onChange={e => setEditDesignNotes(e.target.value)}
                                rows={2}
                                className="w-full bg-keder-gray/50 text-white text-sm rounded px-3 py-2 border border-keder-gray focus:border-keder-yellow outline-none resize-none"
                              />
                            </div>
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <label className="text-gray-400 text-xs block mb-1">Link to</label>
                                <select
                                  value={editDesignLinkType}
                                  onChange={e => { setEditDesignLinkType(e.target.value); setEditDesignLinkId(''); if (e.target.value === 'order') loadDealerOrders(); }}
                                  className="w-full bg-keder-gray/50 text-white text-sm rounded px-3 py-2 border border-keder-gray focus:border-keder-yellow outline-none"
                                >
                                  <option value="">None</option>
                                  <option value="order">Order</option>
                                  <option value="lead">Lead</option>
                                  <option value="estimate">Estimate</option>
                                </select>
                              </div>
                              {editDesignLinkType === 'order' && (
                                <div className="flex-1">
                                  <label className="text-gray-400 text-xs block mb-1">Order</label>
                                  <select
                                    value={editDesignLinkId}
                                    onChange={e => setEditDesignLinkId(e.target.value)}
                                    className="w-full bg-keder-gray/50 text-white text-sm rounded px-3 py-2 border border-keder-gray focus:border-keder-yellow outline-none"
                                  >
                                    <option value="">Select order...</option>
                                    {dealerOrders.map(o => (
                                      <option key={o.id} value={o.orderNumber}>
                                        {o.orderNumber}{o.sidemark ? ` — ${o.sidemark}` : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              {editDesignLinkType && editDesignLinkType !== 'order' && (
                                <div className="flex-1">
                                  <label className="text-gray-400 text-xs block mb-1">ID</label>
                                  <input
                                    type="text"
                                    value={editDesignLinkId}
                                    onChange={e => setEditDesignLinkId(e.target.value)}
                                    className="w-full bg-keder-gray/50 text-white text-sm rounded px-3 py-2 border border-keder-gray focus:border-keder-yellow outline-none"
                                  />
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveEditDesign(d.id)}
                                className="px-4 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm rounded transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingDesignId(null)}
                                className="px-4 py-1.5 bg-keder-gray hover:bg-keder-gray/80 text-white text-sm rounded transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* Action buttons */
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleStartEditDesign(d)}
                              className="px-3 py-1.5 bg-keder-gray hover:bg-keder-gray/80 text-white text-xs rounded transition-colors"
                            >
                              Edit Details
                            </button>
                            {isDeleting ? (
                              <div className="flex gap-1 items-center">
                                <span className="text-red-400 text-xs mr-1">Delete?</span>
                                <button
                                  onClick={() => handleDeleteDesign(d.id)}
                                  className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs rounded transition-colors"
                                >
                                  Yes
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(null)}
                                  className="px-3 py-1.5 bg-keder-gray hover:bg-keder-gray/80 text-white text-xs rounded transition-colors"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirmId(d.id)}
                                className="px-3 py-1.5 bg-red-900 hover:bg-red-700 text-red-300 text-xs rounded transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="card p-4 mb-6 border-l-4 border-l-keder-yellow">
        <h3 className="text-keder-yellow font-semibold mb-2 text-sm">How to Use</h3>
        <div className="text-gray-400 text-sm space-y-0.5">
          <p>1. Upload a photo of the patio opening</p>
          <p>2. Select product type: V-Track (retractable) or Fixed Screen</p>
          {productType === 'vtrack' ? (
            <>
              <p>3. Click 4 corner points to define where the screen goes</p>
              <p>4. Drag corners to fine-tune position</p>
              <p>5. Select fabric type, fabric color, and frame color</p>
            </>
          ) : (
            <>
              <p>3. Click 3 or more points to outline the fixed screen area</p>
              <p>4. Drag corners to fine-tune position</p>
              <p>5. Select fabric type and fabric color</p>
            </>
          )}
          <p>6. Click "Finalize Screen" to lock it in and add another</p>
        </div>
      </div>

      {/* Completed Openings */}
      {completedOpenings.length > 0 && (
        <div className="card p-4 mb-6 border-l-4 border-l-purple-500">
          <h3 className="text-purple-400 font-semibold mb-3">
            Saved Openings ({completedOpenings.length})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {completedOpenings.map((o, i) => (
              <div key={i} className="relative bg-keder-dark rounded-lg overflow-hidden border border-keder-gray">
                <img src={o.imageDataUrl} alt={o.label} className="w-full h-24 object-cover" />
                <div className="p-2">
                  <p className="text-white text-xs font-medium truncate">{o.label}</p>
                </div>
                <button
                  onClick={() => removeOpening(i)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-600 text-white rounded-full text-xs font-bold flex items-center justify-center hover:bg-red-500"
                  title="Remove opening"
                >
                  X
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="mb-6 text-center">
        <div
          className="border-2 border-dashed border-keder-gray rounded-lg p-6 bg-keder-dark hover:border-keder-yellow transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <svg
            className="w-10 h-10 mx-auto text-gray-500 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
            />
          </svg>
          <p className="text-gray-400 text-sm">
            {originalImage ? 'Click to upload a different image' : 'Click to upload a patio image (JPG, PNG)'}
          </p>
        </div>
      </div>

      {/* Status Bar */}
      {originalImage && (
        <div className="flex gap-4 mb-4 flex-wrap">
          <div className="bg-keder-dark border-l-4 border-l-keder-yellow px-4 py-2 rounded-r-lg">
            <span className="text-keder-yellow font-semibold text-sm">
              Screen: {currentScreenNumber}
            </span>
          </div>
          <div className={`bg-keder-dark border-l-4 px-4 py-2 rounded-r-lg ${productType === 'fixed' ? 'border-l-teal-500' : 'border-l-blue-500'}`}>
            <span className={`font-semibold text-sm ${productType === 'fixed' ? 'text-teal-400' : 'text-blue-400'}`}>
              Points: {currentPoints.length} {productType === 'vtrack' ? '/ 4' : `(min 3)`}
            </span>
          </div>
          <div className={`bg-keder-dark border-l-4 px-4 py-2 rounded-r-lg ${productType === 'fixed' ? 'border-l-teal-500' : 'border-l-purple-500'}`}>
            <span className={`font-semibold text-sm ${productType === 'fixed' ? 'text-teal-400' : 'text-purple-400'}`}>
              {productType === 'vtrack' ? 'V-Track' : 'Fixed Screen'}
            </span>
          </div>
        </div>
      )}

      {/* Product Type Selector */}
      {originalImage && (
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Product:</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-keder-gray">
            <button
              onClick={() => { setProductType('vtrack'); setCurrentPoints([]); setFrameColor(''); }}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                productType === 'vtrack'
                  ? 'bg-keder-yellow text-black'
                  : 'bg-keder-dark text-gray-400 hover:text-white'
              }`}
            >
              V-Track
            </button>
            <button
              onClick={() => { setProductType('door'); setCurrentPoints([]); setFrameColor(''); }}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                productType === 'door'
                  ? 'bg-blue-500 text-white'
                  : 'bg-keder-dark text-gray-400 hover:text-white'
              }`}
            >
              Door
            </button>
            <button
              onClick={() => { setProductType('fixed'); setCurrentPoints([]); setFrameColor(''); }}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                productType === 'fixed'
                  ? 'bg-teal-500 text-white'
                  : 'bg-keder-dark text-gray-400 hover:text-white'
              }`}
            >
              Fixed Screen
            </button>
          </div>
          <span className="text-gray-500 text-xs">
            {productType === 'vtrack' ? 'Retractable screen with frame' : productType === 'door' ? 'Door frame with screen mesh' : 'Stapled fabric, no frame or tracks'}
          </span>
        </div>
      )}

      {/* Controls */}
      {originalImage && (
        <div className={`card p-4 mb-4 border-l-4 ${productType === 'door' ? 'border-l-blue-500' : productType === 'fixed' ? 'border-l-teal-500' : 'border-l-keder-yellow'} grid grid-cols-1 ${productType === 'fixed' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4`}>
          {/* Fabric selectors — all types (V-Track screen, Door mesh, Fixed fabric) */}
          {(
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Fabric Type
                </label>
                <select
                  value={fabricType}
                  onChange={(e) => {
                    setFabricType(e.target.value);
                    setFabricColor('');
                  }}
                  className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none"
                >
                  <option value="">-- Select Fabric --</option>
                  <optgroup label="Solar">
                    <option value="omega_pro_3">Omega Pro 3%</option>
                    <option value="omega_pro_5">Omega Pro 5%</option>
                    <option value="omega_pro_10">Omega Pro 10%</option>
                    <option value="twitchell_80">Twitchell 80</option>
                    <option value="twitchell_90">Twitchell 90</option>
                    <option value="twitchell_95">Twitchell 95</option>
                  </optgroup>
                  <optgroup label="Premium Solar">
                    <option value="twitchell_nano_95">Twitchell Nano 95</option>
                    <option value="twitchell_nano_97">Twitchell Nano 97</option>
                    <option value="twitchell_nano_99">Twitchell Nano 99</option>
                    <option value="twitchell_dimout">Twitchell Dimout</option>
                  </optgroup>
                  <optgroup label="Vinyl">
                    <option value="502_vinyl">502 Vinyl</option>
                  </optgroup>
                  <optgroup label="Insect">
                    <option value="nano_55_insect">Twitchell Nano 55 Insect</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Fabric Color
                </label>
                <select
                  value={fabricColor}
                  onChange={(e) => setFabricColor(e.target.value)}
                  disabled={fabricColors.length === 0}
                  className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none disabled:opacity-50"
                >
                  <option value="">
                    {fabricColors.length === 0 ? '-- Select Fabric Type First --' : '-- Select Color --'}
                  </option>
                  {fabricColors.map((c) => (
                    <option key={c.name} value={c.rgba}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Frame Color — V-Track and Door (fixed has no frame) */}
          {(productType === 'vtrack' || productType === 'door') && (
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Frame Color
              </label>
              <select
                value={frameColor}
                onChange={(e) => setFrameColor(e.target.value)}
                className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none"
              >
                <option value="">-- Select Frame --</option>
                {FRAME_COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {originalImage && (
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={undoPoint}
            disabled={currentPoints.length === 0}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg font-semibold text-sm uppercase tracking-wide hover:bg-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Undo Point
          </button>
          <button
            onClick={finalizeScreen}
            disabled={!canFinalize}
            className={`px-4 py-2 rounded-lg font-semibold text-sm uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
              productType === 'door' ? 'bg-blue-500 text-white hover:bg-blue-400'
              : productType === 'fixed' ? 'bg-teal-500 text-white hover:bg-teal-400'
              : 'bg-keder-yellow text-black hover:bg-yellow-400'
            }`}
          >
            {productType === 'door' ? 'Add Door' : productType === 'fixed' ? 'Add Fixed Screen' : 'Finalize Screen'}
          </button>
          <button
            onClick={() => setHideOverlays(!hideOverlays)}
            className={`px-4 py-2 rounded-lg font-semibold text-sm uppercase tracking-wide transition-colors ${
              hideOverlays
                ? 'bg-blue-600 text-white hover:bg-blue-500'
                : 'bg-gray-600 text-white hover:bg-gray-500'
            }`}
          >
            {hideOverlays ? 'Show Labels' : 'Hide Labels'}
          </button>
          {finalizedScreens.length > 0 && (
            <button
              onClick={saveCurrentOpening}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold text-sm uppercase tracking-wide hover:bg-green-500 transition-colors"
            >
              Save Opening & Add Another
            </button>
          )}
          <button
            onClick={resetAll}
            className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold text-sm uppercase tracking-wide hover:bg-red-500 transition-colors"
          >
            Reset All
          </button>
        </div>
      )}

      {/* Canvas */}
      <div className="mb-6 text-center">
        <canvas
          ref={canvasRef}
          className="border-2 border-keder-gray rounded-lg max-w-full bg-keder-dark"
          style={{
            cursor: originalImage ? 'crosshair' : 'default',
            display: originalImage ? 'inline-block' : 'none',
            touchAction: 'none',
          }}
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onClick={handleCanvasClick}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        />
      </div>

      {/* Finalized Screens Summary */}
      {finalizedScreens.length > 0 && (
        <div className="card p-4 mb-6 border-l-4 border-l-green-500">
          <h3 className="text-green-400 font-semibold mb-3">
            Finalized Screens ({finalizedScreens.length})
          </h3>
          <div className="space-y-2">
            {finalizedScreens.map((s, sIdx) => (
              <div
                key={`${s.number}-${sIdx}`}
                className="flex items-center gap-3 bg-keder-dark rounded-lg p-3 border border-keder-gray"
              >
                <span className={`w-7 h-7 flex items-center justify-center font-bold text-sm rounded ${
                  s.productType === 'fixed' ? 'bg-teal-500 text-white' : 'bg-keder-yellow text-black'
                }`}>
                  {s.number}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-medium">{s.label}</p>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      s.productType === 'fixed'
                        ? 'bg-teal-500/20 text-teal-400'
                        : 'bg-keder-yellow/20 text-keder-yellow'
                    }`}>
                      {s.productType === 'fixed' ? 'Fixed' : 'V-Track'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs">
                    {s.productType === 'fixed'
                      ? `${s.points.length} points`
                      : `Frame: ${s.frameColorName}`
                    }
                  </p>
                </div>
                {s.productType === 'vtrack' && (
                  <div
                    className="w-6 h-6 rounded border border-gray-600"
                    style={{ backgroundColor: s.frameColor }}
                    title={`Frame: ${s.frameColorName}`}
                  />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Load screen back into editor for modification
                    setCurrentPoints([...s.points]);
                    setProductType(s.productType);
                    if (s.fabricColor) setFabricColor(s.fabricColor);
                    if (s.frameColor) setFrameColor(s.frameColor);
                    if (s.fabricType) setFabricType(s.fabricType);
                    // Remove from finalized by index
                    setFinalizedScreens(prev => {
                      const updated = [...prev];
                      updated.splice(sIdx, 1);
                      return updated.map((fs, i) => ({ ...fs, number: i + 1, label: `Screen ${i + 1}` }));
                    });
                    setCurrentScreenNumber(finalizedScreens.length);
                  }}
                  className="text-blue-400 hover:text-blue-300 text-xs px-2 py-0.5 border border-blue-500/30 rounded hover:bg-blue-500/10"
                  title="Edit screen"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Remove from finalized by index
                    setFinalizedScreens(prev => {
                      const updated = [...prev];
                      updated.splice(sIdx, 1);
                      return updated.map((fs, i) => ({ ...fs, number: i + 1, label: `Screen ${i + 1}` }));
                    });
                    setCurrentScreenNumber(prev => Math.max(1, prev - 1));
                  }}
                  className="text-red-400 hover:text-red-300 text-xs px-1.5 py-0.5 border border-red-500/30 rounded hover:bg-red-500/10"
                  title="Delete screen"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Design Section */}
      {(finalizedScreens.length > 0 || completedOpenings.length > 0) && (
        <div className="card p-6 border-l-4 border-l-keder-yellow">
          <h3 className="text-keder-yellow font-semibold mb-1">Save Design</h3>
          <p className="text-gray-400 text-xs mb-4">
            {completedOpenings.length > 0
              ? `${completedOpenings.length} opening${completedOpenings.length !== 1 ? 's' : ''} ready${finalizedScreens.length > 0 ? ' + current canvas' : ''}`
              : `${finalizedScreens.length} screen${finalizedScreens.length !== 1 ? 's' : ''} on current image`
            }
          </p>

          {saveSuccess && (
            <div className="mb-4 p-3 bg-green-900/30 border border-green-700 rounded-lg text-green-400 text-sm">
              {saveSuccess}
            </div>
          )}
          {saveError && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
              {saveError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Design Name *
              </label>
              <input
                type="text"
                value={designName}
                onChange={(e) => setDesignName(e.target.value)}
                placeholder="e.g., Smith Patio - 3 screens"
                className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none placeholder-gray-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Link To (Optional)
              </label>
              <select
                value={linkType}
                onChange={(e) => {
                  setLinkType(e.target.value);
                  setLinkId('');
                  setOrderSearch('');
                  if (e.target.value === 'order') loadDealerOrders();
                }}
                className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none"
              >
                <option value="">-- No Link --</option>
                <option value="lead">New Lead</option>
                <option value="order">Existing Order</option>
                <option value="estimate">New Estimate</option>
              </select>
            </div>
          </div>

          {linkType === 'order' && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Select Order
              </label>
              <input
                type="text"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Search by order # or sidemark..."
                className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none placeholder-gray-500 mb-2"
              />
              <div className="max-h-48 overflow-y-auto rounded-lg border border-keder-gray bg-keder-dark">
                {dealerOrders.length === 0 && !ordersLoaded ? (
                  <p className="text-gray-500 text-xs p-3">Loading orders...</p>
                ) : dealerOrders.length === 0 ? (
                  <p className="text-gray-500 text-xs p-3">No orders found.</p>
                ) : (
                  dealerOrders
                    .filter(o => {
                      if (!orderSearch.trim()) return true;
                      const q = orderSearch.toLowerCase();
                      return o.orderNumber.toLowerCase().includes(q) || o.sidemark.toLowerCase().includes(q);
                    })
                    .map(o => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => { setLinkId(o.orderNumber); setOrderSearch(''); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-keder-gray/50 transition-colors flex justify-between items-center ${
                          linkId === o.orderNumber ? 'bg-keder-yellow/10 border-l-2 border-keder-yellow' : ''
                        }`}
                      >
                        <div>
                          <span className="text-white font-medium">{o.orderNumber}</span>
                          {o.sidemark && <span className="text-gray-400 ml-2">— {o.sidemark}</span>}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          o.status === 'draft' ? 'bg-gray-700 text-gray-300' :
                          o.status === 'submitted' ? 'bg-blue-900 text-blue-300' :
                          o.status === 'in_production' ? 'bg-yellow-900 text-yellow-300' :
                          'bg-green-900 text-green-300'
                        }`}>{o.status?.replace(/_/g, ' ')}</span>
                      </button>
                    ))
                )}
              </div>
              {linkId && (
                <p className="text-keder-yellow text-xs mt-1">
                  Selected: <span className="font-medium">{linkId}</span>
                  <button onClick={() => setLinkId('')} className="text-gray-400 hover:text-white ml-2">(clear)</button>
                </p>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Any additional notes about this design..."
              className="w-full px-3 py-2 bg-keder-dark border border-keder-gray rounded-lg text-white text-sm focus:border-keder-yellow focus:outline-none placeholder-gray-500 resize-none"
            />
          </div>

          <button
            onClick={handleSaveDesign}
            disabled={saving || !designName.trim()}
            className="px-6 py-3 bg-keder-yellow text-black rounded-lg font-semibold text-sm uppercase tracking-wide hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : completedOpenings.length > 0 ? `Save All (${completedOpenings.length + (finalizedScreens.length > 0 ? 1 : 0)} Openings)` : 'Save Design'}
          </button>
        </div>
      )}
    </div>
  );
}

export default ScreenDesignerPage;
